import jsPDF from "jspdf";

const base = import.meta.env.BASE_URL.endsWith("/")
  ? import.meta.env.BASE_URL
  : `${import.meta.env.BASE_URL}/`;

const FONT_URLS = {
  regular: `${base}fonts/NotoSans-Regular.ttf`,
  bold: `${base}fonts/NotoSans-Bold.ttf`,
  hebrewRegular: `${base}fonts/NotoSansHebrew-Regular.ttf`,
  hebrewBold: `${base}fonts/NotoSansHebrew-Bold.ttf`,
};

type FontVariant = keyof typeof FONT_URLS;

const fontCache: Partial<Record<FontVariant, string>> = {};

async function fetchFontAsBase64(url: string): Promise<string> {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch font: ${url}`);
  }
  const arrayBuffer = await response.arrayBuffer();
  const bytes = new Uint8Array(arrayBuffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

async function loadFont(variant: FontVariant): Promise<string> {
  if (fontCache[variant]) {
    return fontCache[variant]!;
  }
  const base64 = await fetchFontAsBase64(FONT_URLS[variant]);
  fontCache[variant] = base64;
  return base64;
}

export async function registerFonts(doc: jsPDF): Promise<void> {
  const [regularFont, boldFont, hebrewRegularFont, hebrewBoldFont] =
    await Promise.all([
      loadFont("regular"),
      loadFont("bold"),
      loadFont("hebrewRegular"),
      loadFont("hebrewBold"),
    ]);

  doc.addFileToVFS("NotoSans-Regular.ttf", regularFont);
  doc.addFileToVFS("NotoSans-Bold.ttf", boldFont);
  doc.addFileToVFS("NotoSansHebrew-Regular.ttf", hebrewRegularFont);
  doc.addFileToVFS("NotoSansHebrew-Bold.ttf", hebrewBoldFont);

  doc.addFont("NotoSans-Regular.ttf", "NotoSans", "normal");
  doc.addFont("NotoSans-Bold.ttf", "NotoSans", "bold");
  doc.addFont("NotoSansHebrew-Regular.ttf", "NotoSansHebrew", "normal");
  doc.addFont("NotoSansHebrew-Bold.ttf", "NotoSansHebrew", "bold");
}

const HEBREW_RANGE_REGEX = /[\u0590-\u05FF]/;

function containsHebrew(text: string): boolean {
  return HEBREW_RANGE_REGEX.test(text);
}

export function getFontForText(text: string): string {
  return containsHebrew(text) ? "NotoSansHebrew" : "NotoSans";
}
